/**
 * Vendoré depuis front-end/src/lib/auth.ts (2026-08-04).
 *
 * Divergence volontaire : `email` est optionnel.
 * Les flux `auth/mobile/*` — les seuls utilisés par cette PWA — signent leurs
 * tokens via `issueMobileTokens()`, qui appelle `generateTokens({ sub, roles,
 * organizationId })` sans `email` (back-end/src/modules/auth/auth.service.ts).
 * Le front-end web, lui, passe par `POST /auth/login` qui inclut l'email.
 *
 * Toute autre correction ici doit être reportée dans front-end/ (et inversement).
 */
import { clearTokens, getAccessToken, setTokens, signOut } from './api'

export interface JwtPayload {
  sub: string
  email?: string
  roles: string[]
  organizationId: string | null
  jti: string
  exp: number
  iat: number
}

export interface AuthUser {
  id: string
  email?: string
  roles: string[]
  organizationId: string | null
}

function parseJwt(token: string): JwtPayload | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(base64)) as JwtPayload
  } catch {
    return null
  }
}

export function getAuthUser(): AuthUser | null {
  const token = getAccessToken() // en mémoire — illisible par XSS après un rechargement
  if (!token) return null

  const payload = parseJwt(token)
  if (!payload) return null

  if (payload.exp * 1000 < Date.now()) {
    return null
  }

  return {
    id: payload.sub,
    email: payload.email,
    roles: payload.roles,
    organizationId: payload.organizationId,
  }
}

export function isAuthenticated(): boolean {
  return getAuthUser() !== null
}

export function hasRole(role: string): boolean {
  const user = getAuthUser()
  return user?.roles.includes(role) ?? false
}

export { clearTokens, setTokens, signOut }
